import React, { useState, useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import Loading from '../../Common/Loading';
import CustomAlert from '../../Common/CustomAlert';
import logoImg from '../../Common/Images/KE_Logo.png';
import submit_n from '../../Common/Images/submit_n.gif';
import submit_o from '../../Common/Images/submit_o.gif';
import { useNavigate } from "react-router-dom";



const API_BASE_URL = process.env.REACT_APP_DOCUMENT_LINK;
const ITEMS_PER_PAGE = 50;

interface DiaryEntry {
    endDateTime: string;
    activity: string;
    score: string;
    timeSpent: string;
    attempt: string;
    chapter: string;
    unit: string;
    level: string;
    howDone: string;
}

interface DropdownOption {
    value: string;
    label: string;
}

type SortKey = keyof DiaryEntry;
type SortDirection = 'asc' | 'desc';
type SortType = 'date' | 'number' | 'duration' | 'text';

interface SortConfig {
    key: SortKey;
    direction: SortDirection;
}

interface ColumnDef {
    key: SortKey;
    label: string;
    type: SortType;
    center?: boolean;
    fullWidth?: boolean;
}

const COLUMNS: ColumnDef[] = [
    { key: 'endDateTime', label: 'Date & Time', type: 'date' },
    { key: 'activity', label: 'Activity', type: 'text' },
    { key: 'score', label: 'Score', type: 'number', center: true },
    { key: 'timeSpent', label: 'Time', type: 'duration', center: true },
    { key: 'attempt', label: 'Attempt #', type: 'number', center: true },
    { key: 'chapter', label: 'Chapter', type: 'text' },
    { key: 'unit', label: 'Unit', type: 'text' },
    { key: 'level', label: 'Level', type: 'text' },
    { key: 'howDone', label: 'Selected From', type: 'text', fullWidth: true },
];

// ---------- Sort helpers ----------

// Returns null when the value cannot be interpreted (those rows always go last)
const parseNumber = (value: string): number | null => {
    if (value === null || value === undefined) return null;
    const str = String(value).trim();
    if (!str) return null;

    // Fraction such as "8/10"
    const frac = str.match(/(-?\d+(?:\.\d+)?)\s*\/\s*(\d+(?:\.\d+)?)/);
    if (frac) {
        const denom = parseFloat(frac[2]);
        return denom !== 0 ? (parseFloat(frac[1]) / denom) * 100 : null;
    }

    const num = str.match(/-?\d+(?:\.\d+)?/);
    return num ? parseFloat(num[0]) : null;
};

// Handles "mm:ss", "hh:mm:ss", "5 min 30 sec", or plain numbers
const parseDuration = (value: string): number | null => {
    if (value === null || value === undefined) return null;
    const str = String(value).trim();
    if (!str) return null;

    if (str.includes(':')) {
        const parts = str.split(':').map(p => parseFloat(p));
        if (parts.some(isNaN)) return null;
        return parts.reduce((acc, p) => acc * 60 + p, 0);
    }

    const h = str.match(/(\d+(?:\.\d+)?)\s*h/i);
    const m = str.match(/(\d+(?:\.\d+)?)\s*m/i);
    const s = str.match(/(\d+(?:\.\d+)?)\s*s/i);
    if (h || m || s) {
        return (h ? parseFloat(h[1]) * 3600 : 0) +
               (m ? parseFloat(m[1]) * 60 : 0) +
               (s ? parseFloat(s[1]) : 0);
    }

    return parseNumber(str);
};

// Handles "dd/mm/yyyy hh:mm(:ss) [AM/PM]" (and "-" separators), falls back to Date.parse
const parseDateTime = (value: string): number | null => {
    if (value === null || value === undefined) return null;
    const str = String(value).trim();
    if (!str) return null;

    const m = str.match(
        /^(\d{1,2})[\/\-.](\d{1,2})[\/\-.](\d{2,4})(?:[ T,]+(\d{1,2}):(\d{2})(?::(\d{2}))?\s*([AaPp][Mm])?)?/
    );
    if (m) {
        const day = parseInt(m[1], 10);
        const month = parseInt(m[2], 10) - 1;
        let year = parseInt(m[3], 10);
        if (year < 100) year += 2000;
        let hour = m[4] ? parseInt(m[4], 10) : 0;
        const minute = m[5] ? parseInt(m[5], 10) : 0;
        const second = m[6] ? parseInt(m[6], 10) : 0;
        if (m[7]) {
            const pm = m[7].toLowerCase() === 'pm';
            if (pm && hour < 12) hour += 12;
            if (!pm && hour === 12) hour = 0;
        }
        return new Date(year, month, day, hour, minute, second).getTime();
    }

    const parsed = Date.parse(str);
    return isNaN(parsed) ? null : parsed;
};

const getSortValue = (row: DiaryEntry, col: ColumnDef): number | string | null => {
    const raw = row[col.key];
    switch (col.type) {
        case 'date': return parseDateTime(raw);
        case 'number': return parseNumber(raw);
        case 'duration': return parseDuration(raw);
        default: {
            const s = (raw ?? '').toString().trim().toLowerCase();
            return s === '' ? null : s;
        }
    }
};

const styles = {
    container: {
        position: 'absolute' as 'absolute',
        top: 0, left: 0, right: 0, bottom: 0,
        display: 'flex', flexDirection: 'column' as 'column',
        fontFamily: 'Verdana, Arial, Helvetica, sans-serif', fontSize: '11px',
        backgroundColor: '#FFFFFF', overflow: 'hidden'
    },

    headerSection: {
        backgroundColor: 'white',
        borderBottom: '1px solid #999',
        flexShrink: 0
    },
    topBanner: {
        background: 'linear-gradient(to right, #7196B3, #AEC8D9)',
        padding: '5px 10px',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        color: 'white',
        height: '45px'
    },
    logo: { height: '35px', marginRight: '10px' },
    pageTitle: {
        background: '#2F6089',
        fontSize: '10pt',
        fontWeight: 'bold' as 'bold',
        color: '#FFD700',
        padding: '5px 10px',
        fontFamily: 'Trebuchet MS, Arial, sans-serif'
    },
    familyInfo: {
        fontSize: '11px',
        fontWeight: 'bold' as 'bold',
        color: 'white'
    },

    controlsPanel: {
        padding: '10px',
        backgroundColor: '#E9FEF5',
        borderBottom: '1px solid #A0A0A0'
    },
    controlsTable: {
        width: '100%',
        borderCollapse: 'collapse' as 'collapse',
        fontSize: '11px'
    },
    controlLabel: {
        fontFamily: 'Arial',
        fontWeight: 'bold' as 'bold',
        fontSize: '10pt',
        color: '#2F6089',
        textAlign: 'right' as 'right',
        paddingRight: '5px',
        whiteSpace: 'nowrap' as 'nowrap',
        verticalAlign: 'middle' as 'middle'
    },
    controlCell: {
        textAlign: 'left' as 'left',
        paddingRight: '15px',
        verticalAlign: 'middle' as 'middle'
    },
    select: {
        border: '1px solid #7F9DB9',
        padding: '2px',
        fontSize: '11px',
        fontFamily: 'Verdana',
        width: '100%',
        maxWidth: '200px'
    },

    radioGrid: {
        display: 'flex',
        justifyContent: 'center',
        gap: '150px',
        width: '100%'
    },
    radioColumn: {
        display: 'flex',
        flexDirection: 'column' as 'column',
        gap: '5px',
        alignItems: 'flex-start'
    },
    radioLabel: {
        fontSize: '10pt',
        fontFamily: 'Arial',
        fontWeight: 'bold' as 'bold',
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        marginRight: '5px',
        color: '#333',
        whiteSpace: 'nowrap' as 'nowrap'
    },

    dateInput: {
        border: '1px solid #7F9DB9',
        padding: '1px',
        fontSize: '11px',
        fontFamily: 'Verdana',
        width: '75px',
        marginLeft: '3px'
    },
    dateRangeContainer: {
        display: 'inline-flex',
        alignItems: 'center',
        gap: '3px',
        marginLeft: '5px'
    },

    submitBtn: {
        cursor: 'pointer',
        border: '1px solid #2F6089',
        backgroundColor: '#E0E0E0',
        padding: '2px 15px',
        fontWeight: 'bold' as 'bold',
        fontSize: '11px',
        color: '#2F6089',
        height: '24px'
    },

    tableContainer: {
        flex: 1,
        overflow: 'auto',
        backgroundColor: '#FFFFFF',
        width: '100%',
        textAlign: 'left' as const
    },
    dataTable: {
        borderCollapse: 'collapse' as const,
        fontSize: '11px',
        border: '1px solid rgb(128 160 255)',
        display: 'inline-table',
        whiteSpace: 'nowrap' as const
    },
    th: {
        fontFamily: 'Arial',
        backgroundColor: '#C7DDFD',
        color: '#2859ac',
        fontSize: '10pt',
        padding: '8px 10px',
        fontWeight: 'normal',
        border: '1px solid #a3c0e8',
        textAlign: 'left' as const,
        position: 'sticky' as const,
        top: 0,
        whiteSpace: 'nowrap' as const
    },
    thSortable: {
        cursor: 'pointer',
        userSelect: 'none' as const
    },
    sortArrow: {
        display: 'inline-block',
        marginLeft: '4px',
        fontSize: '8pt',
        minWidth: '8px'
    },
    td: {
        padding: '8px 10px',
        fontFamily: 'Arial',
        fontSize: '10pt',
        borderRight: '1px solid #a3c0e8',
        color: 'black',
        whiteSpace: 'nowrap' as const
    },
    errorBox: {
        color: 'red',
        padding: '10px',
        border: '1px solid red',
        marginBottom: '10px',
        backgroundColor: '#ffe6e6',
        fontSize: '11px'
    },
    paginationBar: {
        fontFamily: 'Arial',
        padding: '8px',
        textAlign: 'center' as 'center',
        backgroundColor: '#C7DDFD',
        borderTop: '1px solid #A0A0A0',
        fontSize: '12pt',
        color: 'black'
    },
    pageLink: {
        cursor: 'pointer',
        margin: '0 5px',
        textDecoration: 'underline',
        color: '#0000FF'
    },
    pageLinkDisabled: {
        margin: '0 5px',
        color: '#999',
        textDecoration: 'none',
        cursor: 'default'
    }
};

const EL_UserDiary: React.FC = () => {
    const [searchParams] = useSearchParams();
    const schoolCode = searchParams.get('SchoolCode') || '';
    const familyCode = searchParams.get('FamilyCode') || '';
    const loginParam = searchParams.get('Login') || '';
    const cnameParam = searchParams.get('cname') || '';
    const calledFrom = searchParams.get('CalledFrom') || '';

    const [hasSearched, setHasSearched] = useState(false);
    const [loading, setLoading] = useState(false);
    const [loadingSubjects, setLoadingSubjects] = useState(false);
    const [loadingLevels, setLoadingLevels] = useState(false);
    const [students, setStudents] = useState<DropdownOption[]>([]);
    const [selectedStudent, setSelectedStudent] = useState<string>('');
    const [subjects, setSubjects] = useState<string[]>([]);
    const [selectedSubject, setSelectedSubject] = useState<string>('');
    const [levels, setLevels] = useState<DropdownOption[]>([]);
    const [selectedLevel, setSelectedLevel] = useState<string>('');
    const [reportData, setReportData] = useState<DiaryEntry[]>([]);
    const [error, setError] = useState<string | null>(null);
    const [isHovered, setIsHovered] = useState(false);

    const [dateRange, setDateRange] = useState('ALL');
    const todayStr = new Date().toISOString().split('T')[0];
    const [customStart, setCustomStart] = useState(todayStr);
    const [customEnd, setCustomEnd] = useState(todayStr);
    const [currentPage, setCurrentPage] = useState(1);
    const [alertInfo, setAlertInfo] = useState({ show: false, message: '' });
    const [sortConfig, setSortConfig] = useState<SortConfig | null>(null);

    const navigate = useNavigate();

    useEffect(() => {
        const fetchStudents = async () => {
            setLoading(true);
            try {
                const response = await fetch(
                    `${API_BASE_URL}api/diary/students/?` +
                    `SchoolCode=${encodeURIComponent(schoolCode)}` +
                    `&FamilyCode=${encodeURIComponent(familyCode)}` +
                    `&Login=${encodeURIComponent(loginParam)}` +
                    `&CalledFrom=${encodeURIComponent(calledFrom)}`
                );
                if (!response.ok) throw new Error('Failed to fetch students');

                const data = await response.json();
                let studentOptions = data.map((s: any) => ({
                    value: s.Login || s.id,
                    label: s.FirstName || s.first_name
                }));

                if (studentOptions.length > 1) {
                    studentOptions.unshift({ value: '', label: '-- Select --' });
                }
                setStudents(studentOptions);

                if (cnameParam && studentOptions.length > 0) {
                    const index = parseInt(cnameParam);
                    if (index >= 0 && index < studentOptions.length) setSelectedStudent(studentOptions[index].value);
                }
                else if (loginParam && loginParam !== 'A') {
                    const found = studentOptions.find((s: any) => s.value === loginParam);
                    if (found) setSelectedStudent(found.value);
                }
                else if (studentOptions.length > 0) {
                    setSelectedStudent(studentOptions[0].value);
                }
            } catch (error: any) { setError(error.message); } finally { setLoading(false); }
        };
        if (schoolCode && familyCode) fetchStudents();
    }, [schoolCode, familyCode, cnameParam, loginParam]);

    useEffect(() => {
        if (!selectedStudent || selectedStudent === '-Select-') { setSubjects([]); return; }
        const fetchSubjects = async () => {
            setLoadingSubjects(true);
            try {
                const url =
                    `${API_BASE_URL}api/diary/subjects/?` +
                    `SchoolCode=${encodeURIComponent(schoolCode)}` +
                    `&FamilyCode=${encodeURIComponent(familyCode)}` +
                    `&Login=${encodeURIComponent(selectedStudent)}` +
                    `&CalledFrom=${encodeURIComponent(calledFrom)}`;
                const response = await fetch(url);
                if (response.ok) {
                    const data = await response.json();
                    setSubjects(data);
                    setSelectedSubject('');
                }
            } catch (err) { console.error(err); }
            finally { setLoadingSubjects(false); }
        };
        fetchSubjects();
    }, [selectedStudent, schoolCode, familyCode]);

    // useEffect(() => {
    //     if (!selectedStudent) { setLevels([]); return; }
    //     const fetchLevels = async () => {
    //         try {
    //             const encodedSubject = encodeURIComponent(selectedSubject);
    //             const url = `${API_BASE_URL}api/diary/levels/?SchoolCode=${schoolCode}&FamilyCode=${familyCode}&Login=${selectedStudent}&Subject=${encodedSubject}`;
    //             const response = await fetch(url);
    //             if (response.ok) {
    //                 const data = await response.json();
    //                 setLevels(data);
    //                 setSelectedLevel('');
    //             }
    //         } catch (err) { console.error(err); }
    //     };
    //     fetchLevels();
    // }, [selectedSubject, selectedStudent, schoolCode, familyCode]);

    useEffect(() => {
        if (!selectedStudent) {
            setLevels([]);
            return;
        }

        const fetchLevels = async () => {
            setLoadingLevels(true);
            try {
                const subject = selectedSubject;

                const url =
                    `${API_BASE_URL}api/diary/levels/?` +
                    `SchoolCode=${encodeURIComponent(schoolCode)}` +
                    `&FamilyCode=${encodeURIComponent(familyCode)}` +
                    `&Login=${encodeURIComponent(selectedStudent)}` +
                    `&Subject=${encodeURIComponent(subject)}`;

                const response = await fetch(url);

                if (response.ok) {
                    const data = await response.json();
                    setLevels(data);
                    setSelectedLevel('');
                }
            } catch (err) {
                console.error(err);
            } finally {
                setLoadingLevels(false);
            }
        };

        fetchLevels();
    }, [selectedSubject, selectedStudent, schoolCode, familyCode]);

    const handleClose = () => {
        if (calledFrom === "SysAdmin") {
            window.close();
        } else if (calledFrom === "Support") {
            if (window.opener) {
                window.close();
            } else {
                navigate(-1);
            }
        } else {
            navigate("/SysAdmin");
        }
    };

    const handleSubmit = async () => {
        if (!selectedStudent || selectedStudent === '-Select-') {
            setAlertInfo({ show: true, message: 'Please select a student.' });
            return;
        }

        setLoading(true);
        setError(null);
        setHasSearched(true);
        setReportData([]);
        setCurrentPage(1);

        try {
            const params = new URLSearchParams({
                SchoolCode: schoolCode,
                FamilyCode: familyCode,
                Login: selectedStudent,
                Subject: selectedSubject,
                Level: selectedLevel,
                DateRange: dateRange,
                CalledFrom: calledFrom
            });

            if (dateRange === 'Period') {
                params.append('StartDate', customStart);
                params.append('EndDate', customEnd);
            }

            const response = await fetch(`${API_BASE_URL}api/get_user_diary_report/?${params.toString()}`);
            if (!response.ok) throw new Error("Failed to generate report.");
            const data = await response.json();
            setReportData(data.report || []);

        } catch (error: any) {
            setError("Failed to fetch report.");
        } finally {
            setLoading(false);
        }
    };

    // Click a header: first click on a new column sorts descending for
    // date/number/time columns (longest/highest/newest first) and ascending
    // for text columns; clicking the same column again reverses it.
    // Sorting only affects the rows on the current page (same as the old VB page).
    const handleSort = (col: ColumnDef) => {
        setSortConfig(prev => {
            if (prev && prev.key === col.key) {
                return { key: col.key, direction: prev.direction === 'asc' ? 'desc' : 'asc' };
            }
            return { key: col.key, direction: col.type === 'text' ? 'asc' : 'desc' };
        });
    };

    const indexOfLastItem = currentPage * ITEMS_PER_PAGE;
    const indexOfFirstItem = indexOfLastItem - ITEMS_PER_PAGE;
    const totalPages = Math.ceil(reportData.length / ITEMS_PER_PAGE);

    // Slice the page first, then sort only those rows
    const currentItems = useMemo(() => {
        const pageRows = reportData.slice(indexOfFirstItem, indexOfLastItem);
        if (!sortConfig) return pageRows;
        const col = COLUMNS.find(c => c.key === sortConfig.key);
        if (!col) return pageRows;

        const dir = sortConfig.direction === 'asc' ? 1 : -1;

        return pageRows
            .map((row, index) => ({ row, index, value: getSortValue(row, col) }))
            .sort((a, b) => {
                // Empty / unparseable values always go to the bottom
                if (a.value === null && b.value === null) return a.index - b.index;
                if (a.value === null) return 1;
                if (b.value === null) return -1;

                let cmp = 0;
                if (typeof a.value === 'number' && typeof b.value === 'number') {
                    cmp = a.value - b.value;
                } else {
                    cmp = String(a.value).localeCompare(String(b.value), undefined, { numeric: true });
                }

                if (cmp === 0) return a.index - b.index;
                return cmp * dir;
            })
            .map(item => item.row);
    }, [reportData, sortConfig, indexOfFirstItem, indexOfLastItem]);

    const changePage = (newPage: number) => {
        if (newPage >= 1 && newPage <= totalPages) {
            setCurrentPage(newPage);
        }
    };

    return (
        <div style={styles.container}>
            <div style={styles.headerSection}>
                <div style={styles.topBanner}>
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                        <img src={logoImg} alt="KE Logo" style={styles.logo} />
                    </div>
                    <div
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            color: 'blue',
                            textDecoration: 'underline',
                            cursor: 'pointer'
                        }}
                        onClick={handleClose}
                    >
                        Close
                    </div>
                </div>
                <div style={styles.pageTitle}>
                    <span style={{ ...styles.pageTitle, color: 'white' }}>User Diary:</span>&nbsp;
                    {/* <span style={{ ...styles.pageTitle, color: '#FDCE47' }}>Family - {familyCode}</span> */}
                    <span style={{ ...styles.pageTitle, color: '#FDCE47' }}>Family - {sessionStorage.getItem('surname')}</span>
                </div>

                <div style={styles.controlsPanel}>
                    <table style={styles.controlsTable}>
                        <tbody>
                            <tr>
                                <td style={{ ...styles.controlLabel, width: '5%' }}>Child</td>
                                <td style={{ ...styles.controlCell, width: '25%' }}>
                                    <select style={styles.select} value={selectedStudent} onChange={(e) => setSelectedStudent(e.target.value)}
                                    // disabled={loginParam !== 'A' && loginParam !== ''}
                                    >
                                        {students.map((s, idx) => <option key={idx} value={s.value}>{s.label}</option>)}
                                    </select>
                                </td>

                                <td style={{ ...styles.controlLabel, width: '5%' }}>Subject</td>
                                <td style={{ ...styles.controlCell, width: '25%' }}>
                                    <select style={styles.select} value={selectedSubject} onChange={(e) => setSelectedSubject(e.target.value)}>
                                        <option value="">ALL</option>
                                        {subjects.map(sub => <option key={sub} value={sub}>{sub}</option>)}
                                    </select>
                                </td>

                                <td style={{ ...styles.controlLabel, width: '5%' }}>Level</td>
                                <td style={{ ...styles.controlCell, width: '25%' }}>
                                    <select style={styles.select} value={selectedLevel} onChange={(e) => setSelectedLevel(e.target.value)}>
                                        <option value="">ALL</option>
                                        {levels.map(l => <option key={l.value} value={l.value}>{l.label}</option>)}
                                    </select>
                                </td>
                                <td></td>
                            </tr>

                            <tr><td colSpan={7} style={{ height: '8px' }}></td></tr>

                            <tr>
                                <td style={{ ...styles.controlLabel, verticalAlign: 'top', paddingTop: '4px' }}></td>
                                <td colSpan={5} style={styles.controlCell}>
                                    <div style={styles.radioGrid}>

                                        <div style={styles.radioColumn}>
                                            <label style={styles.radioLabel}>
                                                <input type="radio" name="pd" value="Today" checked={dateRange === 'Today'} onChange={(e) => setDateRange(e.target.value)} />
                                                &nbsp;Today
                                            </label>
                                            <label style={styles.radioLabel}>
                                                <input type="radio" name="pd" value="Yesterday" checked={dateRange === 'Yesterday'} onChange={(e) => setDateRange(e.target.value)} />
                                                &nbsp;Yesterday
                                            </label>
                                        </div>

                                        <div style={styles.radioColumn}>
                                            <label style={styles.radioLabel}>
                                                <input type="radio" name="pd" value="This Week" checked={dateRange === 'This Week'} onChange={(e) => setDateRange(e.target.value)} />
                                                &nbsp;This Week
                                            </label>
                                            <label style={styles.radioLabel}>
                                                <input type="radio" name="pd" value="Last Week" checked={dateRange === 'Last Week'} onChange={(e) => setDateRange(e.target.value)} />
                                                &nbsp;Last Week
                                            </label>
                                        </div>

                                        <div style={styles.radioColumn}>
                                            <label style={styles.radioLabel}>
                                                <input type="radio" name="pd" value="This Month" checked={dateRange === 'This Month'} onChange={(e) => setDateRange(e.target.value)} />
                                                &nbsp;This Month
                                            </label>
                                            <label style={styles.radioLabel}>
                                                <input type="radio" name="pd" value="Last Month" checked={dateRange === 'Last Month'} onChange={(e) => setDateRange(e.target.value)} />
                                                &nbsp;Last Month
                                            </label>
                                        </div>

                                        <div style={styles.radioColumn}>
                                            <label style={styles.radioLabel}>
                                                <input type="radio" name="pd" value="ALL" checked={dateRange === 'ALL'} onChange={(e) => setDateRange(e.target.value)} />
                                                &nbsp;ALL
                                            </label>
                                            <div style={{ display: 'flex', alignItems: 'center' }}>
                                                <label style={styles.radioLabel}>
                                                    <input type="radio" name="pd" value="Period" checked={dateRange === 'Period'} onChange={(e) => setDateRange(e.target.value)} />
                                                    &nbsp;Period
                                                </label>

                                                {dateRange === 'Period' && (
                                                    <div style={styles.dateRangeContainer}>
                                                        <input type="date" style={styles.dateInput} value={customStart} onChange={(e) => setCustomStart(e.target.value)} />
                                                        <span>to</span>
                                                        <input type="date" style={styles.dateInput} value={customEnd} onChange={(e) => setCustomEnd(e.target.value)} />
                                                    </div>
                                                )}
                                            </div>
                                        </div>

                                    </div>
                                </td>
                                <td style={{ textAlign: 'right', verticalAlign: 'bottom' }}>
                                    <img
                                        src={isHovered ? submit_o : submit_n}
                                        alt="Submit"
                                        onClick={handleSubmit}
                                        onMouseEnter={() => setIsHovered(true)}
                                        onMouseLeave={() => setIsHovered(false)}
                                        style={{ cursor: 'pointer' }}
                                    />
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>

            {(loading || loadingSubjects || loadingLevels) && <div style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(255,255,255,0.7)', zIndex: 100, display: 'flex', justifyContent: 'center', alignItems: 'center' }}><Loading /></div>}
            {error && <div style={styles.errorBox}>{error}</div>}

            {hasSearched && (
                <>
                    <div style={styles.tableContainer}>
                        <table style={styles.dataTable}>
                            <thead>
                                <tr>
                                    <th style={styles.th}>#</th>
                                    {COLUMNS.map(col => {
                                        const isActive = sortConfig?.key === col.key;
                                        return (
                                            <th
                                                key={col.key}
                                                style={{
                                                    ...styles.th,
                                                    ...styles.thSortable,
                                                    ...(col.center ? { textAlign: 'center' as const } : {}),
                                                    ...(col.fullWidth ? { width: '100%' } : {})
                                                }}
                                                onClick={() => handleSort(col)}
                                                title={`Click to sort by ${col.label}`}
                                            >
                                                {col.label}
                                                <span style={styles.sortArrow}>
                                                    {isActive ? (sortConfig!.direction === 'asc' ? '▲' : '▼') : ''}
                                                </span>
                                            </th>
                                        );
                                    })}
                                </tr>
                            </thead>
                            <tbody>
                                {currentItems.length === 0 ? (
                                    <tr><td colSpan={10} style={{ textAlign: 'center', padding: '20px', color: '#666' }}></td></tr>
                                ) : (
                                    currentItems.map((row, idx) => (
                                        <tr key={idx} style={{ backgroundColor: idx % 2 === 0 ? '#F4FAFF' : 'white' }}>
                                            <td style={styles.td}>{indexOfFirstItem + idx + 1}</td>
                                            <td style={styles.td}>{row.endDateTime}</td>
                                            <td style={styles.td}>{row.activity}</td>
                                            <td style={{ ...styles.td, textAlign: 'center' }}>{row.score}</td>
                                            <td style={{ ...styles.td, textAlign: 'center' }}>{row.timeSpent}</td>
                                            <td style={{ ...styles.td, textAlign: 'center' }}>{row.attempt}</td>
                                            <td style={styles.td}>{row.chapter}</td>
                                            <td style={styles.td}>{row.unit}</td>
                                            <td style={styles.td}>{row.level}</td>
                                            <td style={{ ...styles.td, width: "100%" }}>{row.howDone}</td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>

                    {reportData.length > 0 && (
                        <div style={styles.paginationBar}>
                            <span
                                style={currentPage === 1 ? styles.pageLinkDisabled : styles.pageLink}
                                onClick={() => changePage(1)}
                            >
                                &lt;&lt;
                            </span>
                            <span
                                style={currentPage === 1 ? styles.pageLinkDisabled : styles.pageLink}
                                onClick={() => changePage(currentPage - 1)}
                            >
                                &lt;
                            </span>

                            <span style={{ color: 'black', margin: '0 5px' }}>
                                {currentPage} of {totalPages}
                            </span>

                            <span
                                style={currentPage === totalPages ? styles.pageLinkDisabled : styles.pageLink}
                                onClick={() => changePage(currentPage + 1)}
                            >
                                &gt;
                            </span>
                            <span
                                style={currentPage === totalPages ? styles.pageLinkDisabled : styles.pageLink}
                                onClick={() => changePage(totalPages)}
                            >
                                &gt;&gt;
                            </span>
                        </div>
                    )}
                </>
            )}
            {alertInfo.show && (
                <CustomAlert
                    message={alertInfo.message}
                    onClose={() => setAlertInfo({ ...alertInfo, show: false })}
                />
            )}
        </div>
    );
};

export default EL_UserDiary;